import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Choose a different page uniformly, including a partially filled final page.
 * @param {number} pages
 * @param {number} currentPage
 * @param {() => number} [random]
 * @returns {number}
 */
export function chooseDiscoveryPage(pages, currentPage, random = Math.random) {
  if (!Number.isSafeInteger(pages) || pages < 1) throw new RangeError('Invalid page count');
  if (pages === 1) return 1;
  const sample = random();
  if (!Number.isFinite(sample) || sample < 0 || sample >= 1) throw new RangeError('Invalid random sample');
  const hasCurrent = Number.isSafeInteger(currentPage) && currentPage >= 1 && currentPage <= pages;
  const candidate = Math.floor(sample * (pages - Number(hasCurrent))) + 1;
  return hasCurrent && candidate >= currentPage ? candidate + 1 : candidate;
}

/**
 * Preserve the current locale and preview parameters without changing browser history.
 * @param {string} href
 * @param {string} sectionId
 * @param {string} pageParam
 * @param {number} page
 * @returns {URL}
 */
export function discoveryURL(href, sectionId, pageParam, page) {
  if (!/^[A-Za-z0-9_-]+$/.test(sectionId) || !/^page(?:_[A-Za-z0-9_]+)?$/.test(pageParam)) {
    throw new TypeError('Invalid section pagination metadata');
  }
  if (!Number.isSafeInteger(page) || page < 1) throw new RangeError('Invalid page');
  const url = new URL(href);
  url.hash = '';
  url.searchParams.delete('sections');
  url.searchParams.delete('sections[]');
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set(pageParam, String(page));
  return url;
}

/**
 * @typedef {{ grid: HTMLElement, shuffleButton: HTMLButtonElement, status: HTMLElement, showing: HTMLElement }} DiscoveryRefs
 * @extends {Component<DiscoveryRefs>}
 */
class BHGProductDiscovery extends Component {
  requiredRefs = ['grid', 'shuffleButton', 'status', 'showing'];
  /** @type {AbortController | null} */
  #activeRequest = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timeout = null;
  /** @type {AbortController | null} */
  #automaticController = null;
  /** @type {number | null} */
  #idleCallbackId = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #idleTimer = null;
  #nearViewport = false;
  #busy = false;
  #initialAttempted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#activeRequest?.abort();
    this.#activeRequest = null;
    clearTimeout(this.#timeout);
    this.#timeout = null;
    this.#busy = false;
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelAutomatic();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#activeRequest?.abort();
    this.#activeRequest = null;
    clearTimeout(this.#timeout);
    this.#timeout = null;
    this.#busy = false;
  }

  #initialize() {
    this.#cancelAutomatic();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#nearViewport = false;
    const multiplePages = Number(this.refs.grid.dataset.pages) > 1;
    this.refs.shuffleButton.hidden = !multiplePages;
    this.refs.shuffleButton.disabled = false;
    this.refs.grid.removeAttribute('aria-busy');
    if (!multiplePages || this.#initialAttempted || this.dataset.randomizeInitial !== 'true' || window.Shopify?.designMode) return;
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver((entries, observer) => {
      if (observer !== this.#observer || !this.isConnected || this.#initialAttempted) return;
      this.#nearViewport = entries.some((entry) => entry.isIntersecting);
      if (this.#nearViewport) this.#scheduleAutomatic();
      else this.#cancelAutomatic();
    }, { rootMargin: '0px 0px 500px 0px' });
    this.#observer.observe(this);
  }

  #cancelAutomatic() {
    this.#automaticController?.abort();
    this.#automaticController = null;
    if (this.#idleCallbackId !== null) window.cancelIdleCallback(this.#idleCallbackId);
    this.#idleCallbackId = null;
    clearTimeout(this.#idleTimer);
    this.#idleTimer = null;
  }

  #scheduleAutomatic() {
    if (this.#automaticController || this.#initialAttempted) return;
    const controller = new AbortController();
    this.#automaticController = controller;
    const run = () => {
      if (controller.signal.aborted || this.#automaticController !== controller || !this.isConnected) return;
      this.#idleCallbackId = null;
      this.#idleTimer = null;
      this.#automaticController = null;
      controller.abort();
      if (!this.#nearViewport || this.#initialAttempted) return;
      this.#initialAttempted = true;
      this.#observer?.disconnect();
      this.#observer = null;
      // A visitor already using these links keeps the selection they started with.
      if (!this.refs.grid.contains(document.activeElement)) this.#loadRandomPage(true);
    };
    const scheduleIdle = () => {
      if (controller.signal.aborted || this.#automaticController !== controller) return;
      // Keep the first six server-rendered cards while critical page resources load.
      // Browsers without cancellable idle callbacks still yield once after page load.
      if (typeof window.requestIdleCallback === 'function' && typeof window.cancelIdleCallback === 'function') {
        this.#idleCallbackId = window.requestIdleCallback(run);
      } else {
        this.#idleTimer = setTimeout(run, 0);
      }
    };
    if (document.readyState === 'complete') scheduleIdle();
    else window.addEventListener('load', scheduleIdle, { once: true, signal: controller.signal });
  }

  /** Shuffle from the persistent native button without replacing or moving its focus. @param {Event} event */
  shuffle(event) {
    event.preventDefault();
    this.#initialAttempted = true;
    this.#cancelAutomatic();
    this.#observer?.disconnect();
    return this.#loadRandomPage(false);
  }

  /** @param {boolean} automatic */
  async #loadRandomPage(automatic) {
    if (this.#busy || !this.isConnected) return;
    const { grid, shuffleButton, status } = this.refs;
    const pages = Number(grid.dataset.pages);
    if (pages < 2) return;
    const restoreButtonFocus = document.activeElement === shuffleButton;
    const controller = new AbortController();
    this.#activeRequest = controller;
    this.#busy = true;
    shuffleButton.disabled = true;
    grid.setAttribute('aria-busy', 'true');
    if (!automatic) status.textContent = this.dataset.loading || '';
    const timeout = setTimeout(() => controller.abort(), 15000);
    this.#timeout = timeout;

    try {
      const nextPage = chooseDiscoveryPage(pages, Number(grid.dataset.page));
      const url = discoveryURL(window.location.href, this.dataset.sectionId || '', grid.dataset.pageParam || '', nextPage);
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const html = await response.text();
      if (controller.signal.aborted || this.#activeRequest !== controller || !this.isConnected) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(this.id);
      const nextGrid = replacement?.querySelector('[ref="grid"]');
      const nextCards = nextGrid?.querySelectorAll('article[data-product-id]');
      if (!(replacement instanceof HTMLElement) || !(nextGrid instanceof HTMLElement) ||
          replacement.dataset.collection !== this.dataset.collection || replacement.dataset.sectionId !== this.dataset.sectionId ||
          Number(nextGrid.dataset.page) !== nextPage || !nextCards?.length || nextCards.length > 6 ||
          Number(nextGrid.dataset.cardCount) !== nextCards.length) {
        throw new Error('Unexpected discovery section response');
      }
      if ([...nextCards].some((card) => !card.querySelector('img[src]') || !card.querySelector('a[href]'))) {
        throw new Error('Incomplete discovery card');
      }
      // Do not replace a product link the visitor focused while the request was pending.
      if (grid.contains(document.activeElement)) {
        status.textContent = '';
        return;
      }
      // Only the two keyed targets (cards and collection count) change. Their owner stylesheet
      // is already present; controls, live status and focused button remain untouched.
      await morphSection(this.dataset.sectionId, html, 'hydration');
      if (controller.signal.aborted || this.#activeRequest !== controller || !this.isConnected) return;
      this.refs.showing.textContent = this.refs.grid.dataset.showing || '';
      if (!automatic) status.textContent = this.refs.grid.dataset.loaded || '';
      shuffleButton.hidden = Number(this.refs.grid.dataset.pages) < 2;
    } catch (_error) {
      if (this.isConnected && this.#activeRequest === controller) {
        status.textContent = this.dataset.error || '';
      }
    } finally {
      clearTimeout(timeout);
      if (this.#timeout === timeout) this.#timeout = null;
      if (this.#activeRequest === controller) {
        this.#activeRequest = null;
        this.#busy = false;
        if (this.isConnected) {
          shuffleButton.disabled = false;
          this.refs.grid.removeAttribute('aria-busy');
          if (restoreButtonFocus && document.activeElement === document.body) shuffleButton.focus({ preventScroll: true });
        }
      }
    }
  }
}

if (!customElements.get('bhg-product-discovery')) {
  customElements.define('bhg-product-discovery', BHGProductDiscovery);
}
